import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { CartButton } from "./Cart";
export default function Header({ data, ribbonActive = true }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (e, href) => {
    if (href.startsWith("/#")) {
      const hash = href.slice(1); // "#foo"
      if (location.pathname !== "/") {
        e.preventDefault();
        navigate("/" + hash);
      }
      // else: let anchor click bubble to SmoothScroll interceptor
    }
    setOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.6, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed left-0 right-0 z-50 transition-[background,border,padding,top] duration-500 ${
        ribbonActive ? "top-10 md:top-11" : "top-0"
      } ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl saturate-150 border-b border-white/10 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="brand-logo"
          className="group flex items-center gap-3 select-none"
        >
          <span className="flex items-center">
            <motion.span
              className="font-logo text-xl md:text-2xl uppercase text-accent inline-block cursor-pointer drop-shadow-[0_0_18px_rgba(250,204,21,0.25)]"
              whileHover={{ y: -3, rotate: -4, color: "#FDE68A" }}
              whileTap={{ scale: 0.85, rotate: 12 }}
              transition={{ type: "spring", stiffness: 500, damping: 12 }}
              drag
              dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
              dragElastic={0.35}
              dragTransition={{ bounceStiffness: 500, bounceDamping: 15 }}
            >
              {data.brand.name.charAt(0)}
            </motion.span>
            <motion.span
              className="font-logo text-xl md:text-2xl uppercase text-accent inline-block drop-shadow-[0_0_18px_rgba(250,204,21,0.25)]"
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9, duration: 0.5 }}
            >
              {data.brand.name.slice(1)}
            </motion.span>
          </span>
          <motion.span
            className="text-lg font-heading inline-block cursor-pointer text-[#38BDF8]"
            whileHover={{ rotate: 180, scale: 1.25 }}
            whileTap={{ rotate: 720, scale: 0.85 }}
            transition={{ type: "spring", stiffness: 260, damping: 14 }}
            drag
            dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
            dragElastic={0.6}
            dragTransition={{ bounceStiffness: 400, bounceDamping: 12 }}
          >
            ★
          </motion.span>
        </Link>

        <nav
          data-testid="primary-nav"
          className="hidden lg:flex items-center gap-10"
        >
          {data.nav.map((item, i) => {
            const href = item.href || `/#${item.id}`;
            const isRoute = !href.includes("#");
            const cls = "relative font-body text-[13px] tracking-[0.25em] uppercase text-white/70 hover:text-white transition-colors duration-300 group";
            const inner = (
              <>
                <span className="text-accent/70 mr-2 text-[10px]">0{i + 1}</span>
                {item.label}
                <span className="absolute -bottom-2 left-0 h-px w-0 bg-accent transition-all duration-500 group-hover:w-full" />
              </>
            );
            return isRoute ? (
              <Link key={item.id} to={href} data-testid={`nav-${item.id}`} className={cls}>{inner}</Link>
            ) : (
              <a key={item.id} href={href} data-testid={`nav-${item.id}`} className={cls} onClick={(e) => handleNavClick(e, href)}>{inner}</a>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <CartButton />
          <div
            data-testid="ps-partner-badge"
            className="hidden md:flex items-center gap-2 border border-white/15 px-3 py-2 backdrop-blur-md bg-white/[0.02]"
          >
            <span className="font-heading text-sm tracking-[0.15em] text-white/90">
              Xbox
            </span>
            <span className="w-px h-3 bg-white/20" />
            <span className="font-body text-[10px] tracking-[0.3em] uppercase text-accent">
              Partner
            </span>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 border border-white/10 hover:border-white/40 transition-colors"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {data.nav.map((item, i) => {
                const href = item.href || `/#${item.id}`;
                const isRoute = !href.includes("#");
                const cls = "font-heading text-2xl uppercase tracking-wider text-white/90 hover:text-accent transition-colors";
                const inner = (<><span className="text-accent/60 text-sm mr-3">0{i + 1}</span>{item.label}</>);
                return isRoute ? (
                  <Link key={item.id} to={href} onClick={() => setOpen(false)} className={cls}>{inner}</Link>
                ) : (
                  <a key={item.id} href={href} onClick={(e) => handleNavClick(e, href)} className={cls}>{inner}</a>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
